import { Avatar } from '@mantine/core';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { ThemeColor } from '@vrplatform/theme';
import type { ReactNode } from 'react';
import { Badge, type BadgeProps } from './Badge';

export type StatusBadgeStatus =
  | 'success'
  | 'paused'
  | 'archived'
  | 'suspended'
  | 'pending'
  | 'invited'
  | 'expired'
  | 'missing'
  | 'cancelled'
  | 'info'
  | 'blocked'
  | 'unpaid'
  | 'underpaid'
  | 'overpaid'
  | 'shared'
  | 'managed'
  | 'cohost'
  | 'error'
  | 'disabled'
  | 'scheduled'
  | 'highlighted'
  | 'review'
  | 'posted';

type StatusBadgeConfig = {
  color: ThemeColor;
  icon: IconDefinition;
  iconColor?: BadgeProps['leftIconColor'];
  label: string;
  textColor?: string;
};

const STATUS_BADGE_CONFIG: Record<StatusBadgeStatus, StatusBadgeConfig> = {
  success: {
    color: 'green',
    icon: 'CheckCircleIcon',
    iconColor: '#16A34A',
    label: 'Success',
    textColor: '#333333',
  },
  paused: { color: 'gray', icon: 'PauseIcon', label: 'Inactive' },
  archived: {
    color: 'amber',
    icon: 'CrossCircleIcon',
    iconColor: '#F59E0B',
    label: 'Archived',
    textColor: '#737373',
  },
  suspended: { color: 'orange', icon: 'PauseIcon', label: 'Suspended' },
  pending: { color: 'yellow', icon: 'TimeIcon', label: 'Pending' },
  invited: {
    color: 'green',
    icon: 'CheckCircleIcon',
    iconColor: '#16A34A',
    label: 'Invited',
    textColor: '#333333',
  },
  expired: { color: 'orange', icon: 'TimeIcon', label: 'Expired' },
  missing: { color: 'gray', icon: 'MinusCircleIcon', label: 'Missing' },
  cancelled: {
    color: 'amber',
    icon: 'CrossCircleIcon',
    iconColor: '#F59E0B',
    label: 'Canceled',
    textColor: '#737373',
  },
  info: { color: 'blue', icon: 'InfoIcon', label: 'Info' },
  blocked: { color: 'yellow', icon: 'Lock2Icon', label: 'Blocked' },
  unpaid: {
    color: 'amber',
    icon: 'CrossCircleIcon',
    iconColor: '#F59E0B',
    label: 'Unpaid',
    textColor: '#737373',
  },
  underpaid: {
    color: 'yellow',
    icon: 'CircleDollarIcon',
    label: 'Underpaid',
  },
  overpaid: {
    color: 'orange',
    icon: 'CircleDollarIcon',
    label: 'Overpaid',
  },
  shared: { color: 'blue', icon: 'UsersIcon', label: 'Shared' },
  managed: {
    color: 'violet',
    icon: 'BriefcaseIcon',
    iconColor: '#8B5CF6',
    label: 'Managed',
    textColor: '#333333',
  },
  cohost: {
    color: 'green',
    icon: 'UsersIcon',
    iconColor: '#16A34A',
    label: 'Co-host',
    textColor: '#333333',
  },
  error: { color: 'red', icon: 'AlertTriangleIcon', label: 'Error' },
  disabled: { color: 'gray', icon: 'CrossCircleIcon', label: 'Disabled' },
  scheduled: {
    color: 'teal',
    icon: 'TimeIcon',
    label: 'Scheduled',
  },
  highlighted: { color: 'teal', icon: 'CheckCircleIcon', label: 'Ready' },
  review: { color: 'blue', icon: 'EyeIcon', label: 'In review' },
  posted: { color: 'cyan', icon: 'CheckCircleIcon', label: 'Posted' },
};

type DistributiveOmit<T, TKeys extends PropertyKey> = T extends unknown
  ? Omit<T, Extract<keyof T, TKeys>>
  : never;

export type StatusBadgeProps = DistributiveOmit<
  BadgeProps,
  | 'appearance'
  | 'backgroundColor'
  | 'borderColor'
  | 'borderWidth'
  | 'children'
  | 'color'
  | 'fontSize'
  | 'height'
  | 'leftIcon'
  | 'leftIconColor'
  | 'paddingInline'
  | 'radius'
  | 'rightIcon'
  | 'shadow'
  | 'textColor'
  | 'variant'
> & {
  additionalAvatars?: ReactNode;
  label?: ReactNode;
  status: StatusBadgeStatus;
};

export const StatusBadge = ({
  additionalAvatars,
  label,
  size,
  status,
  tooltip,
  ...props
}: StatusBadgeProps) => {
  const config = STATUS_BADGE_CONFIG[status];
  const iconColor =
    config.iconColor ||
    ((currentTheme) => currentTheme.colors[config.color][6]);

  return (
    <Badge
      {...props}
      appearance="tag"
      color={config.color}
      leftIcon={
        additionalAvatars ? (
          <Avatar.Group spacing={5} style={{ alignItems: 'center' }}>
            {additionalAvatars}
            <Avatar
              radius="xl"
              size={16}
              style={{ overflow: 'visible' }}
              variant="transparent"
            >
              <Icon
                color={iconColor}
                icon={config.icon}
                size={12}
                strokeWidth={2}
              />
            </Avatar>
          </Avatar.Group>
        ) : (
          config.icon
        )
      }
      leftIconColor={iconColor}
      size={size ?? 'md'}
      textColor={config.textColor}
      tooltip={tooltip}
    >
      {label ?? config.label}
    </Badge>
  );
};
